import uuid from "./uuidInterface";
import { COOKIE_VERSION, DEFAULT_COOKIE_AGE, DEFAULT_DOMAIN, DEFAULT_DOMAIN_ESC, PERMANENT_COOKIE_AGE, PERMANENT_COOKIE_NAME } from "./constants/cookie-constants";
import CookieDefinitions from "./definitions/cookie-definitions"
import { Log } from "./logger";
import { isESCPartition } from "./utils";

export interface ValidationFields {
    domain?: string;
    log?: Log
}

function removeDotPrefix(domain: string): string {
    if (domain.charAt(0) === '.')
        // remove '.' from the domain
        domain = domain.slice(1);
    return domain;
}

export function validateConfiguration({ domain = DEFAULT_DOMAIN, log }: ValidationFields, source: string) {
    const cleanedDomain = removeDotPrefix(domain);
    const hostname = window.location.hostname;
    if (!hostname.endsWith(cleanedDomain)) {
        // publish metric that captures how many users tried to set their preference but failed
        const error = log ? log("error") : () => { };
        error("domainMismatch", { detail: "Domain mismatch", source: source, configuredDomain: domain, actualDomain: hostname })

        console.error(`Shortbread failed to set user's cookie preference because the domain name that was passed in does not match the hostname of the application. 
        Configured domain: ${domain}.
        Actual domain: ${hostname}.
        As a fallback, Shortbread is only allowing 'essential' cookies to be used.`);
        return false;
    }
    return true;
}

// Check if the permanent consent cookie value is valid
function isValidPermanentConsentCookie(value: any): boolean {
    // Permanent cookie should be an object with a ccba property that is 0 or 1
    // and a version property that is a string
    return value && 
           typeof value === 'object' && 
           (value.ccba === 0 || value.ccba === 1) &&
           typeof value.v === 'string';
}

// Undefined when no valid choice is recorded, so the caller can mirror advertising.
function getPermanentCcbaValue(cookiesString: string): boolean | undefined {
    const value = cookiesString.match(`(^|;)\\s*${PERMANENT_COOKIE_NAME}\\s*=\\s*([^;]+)`);
    if (value && value.length > 0) {
        try {
            const permanentConsent = JSON.parse(atob(value[value.length - 1]));
            if (isValidPermanentConsentCookie(permanentConsent)) {
                return permanentConsent.ccba === 1;
            }
        } catch (err) {
            return undefined;
        }
    }
    return undefined;
}

function unpack(c: CookieDefinitions.CompressedConsentCookie): Omit<CookieDefinitions.InternalConsentCookie, 'ccba'> {
    return {
        essential: c.e === 1,
        performance: c.p === 1,
        functional: c.f === 1,
        advertising: c.a === 1,
        id: c.i,
        version: c.v
    };
}

function pack(c: CookieDefinitions.InternalConsentCookie): CookieDefinitions.CompressedConsentCookie {
    // ccba is now stored in a separate cookie, not included in main cookie
    return {
        e: c.essential ? 1 : 0,
        p: c.performance ? 1 : 0,
        f: c.functional ? 1 : 0,
        a: c.advertising ? 1 : 0,
        i: c.id,
        v: c.version
    };
}

function isValidCookie(cookie: any): cookie is CookieDefinitions.CompressedConsentCookie {
    // Also ensure essential is always true
    if (cookie.e !== 1) return false;
    if (typeof cookie.p !== "number") return false;
    if (typeof cookie.f !== "number") return false;
    if (typeof cookie.a !== "number") return false;
    if (typeof cookie.i !== "string") return false;
    return typeof cookie.v === "string";
}

function getConsentCookieInternal(cookiesString: string, log?: Log): CookieDefinitions.InternalConsentCookie | undefined {
    const value = cookiesString.match("(^|;)\\s*awsccc\\s*=\\s*([^;]+)");
    const error = log ? log("error") : () => { };
    if (value && value.length > 0) {
        try {
            const decoded = JSON.parse(atob(value[value.length - 1]));
            if (isValidCookie(decoded)) {
                const baseCookie = unpack(decoded);
                // An absent cross-context choice mirrors advertising; a cross-context opt-out also withholds advertising.
                const ccba = getPermanentCcbaValue(cookiesString) ?? baseCookie.advertising;
                return {
                    ...baseCookie,
                    advertising: baseCookie.advertising && ccba,
                    ccba
                };
            }
            error("getCookie", { detail: "Cookie format is not valid", cookie: decoded })
            return undefined;
        } catch (err) {
            error("getCookie", { detail: "Error parsing cookie", cookie: value[value.length - 1] })
            return undefined;
        }
    }
    return undefined;
}

export function getConsentCookie(cookiesString = () => document.cookie, log?: Log): CookieDefinitions.ConsentCookie | undefined {
    const value = getConsentCookieInternal(cookiesString(), log);
    if (value) {
        return {
            essential: value.essential,
            performance: value.performance,
            functional: value.functional,
            advertising: value.advertising,
            ccba: value.ccba
        }
    }
    return undefined
}

export function getId(cookiesString = () => document.cookie): string | undefined {
    const cookieValue = getConsentCookieInternal(cookiesString());
    if (cookieValue && cookieValue.id) {
        return cookieValue.id
    }
    return undefined
}

function defaultWriter(cookie: string) {
    document.cookie = cookie;
    return;
}

export function setConsentCookie(
    consent: CookieDefinitions.ConsentCookie,
    domain?: string,
    maxAge = DEFAULT_COOKIE_AGE,
    uuidInterface: (log?: Log, uuidGenerator?: () => string, uuidFallback?: () => string) => string = uuid,
    writer = defaultWriter,
    configurationValidator = validateConfiguration,
    log?: Log,
    uuidGenerator?: () => string,
    uuidFallback?: () => string,
): CookieDefinitions.InternalConsentCookie {
    if (domain === undefined) {
        domain = isESCPartition() ? DEFAULT_DOMAIN_ESC : DEFAULT_DOMAIN;
    }
    configurationValidator({ domain, log }, 'customize');
    const id = getId() || uuidInterface(log, uuidGenerator, uuidFallback);
    const consentCookie = {
        ...consent,
        id, // use id if available, else generate it
        version: COOKIE_VERSION
    }
    const packedCookie = pack(consentCookie)
    const isIframe = window.self !== window.top;
    if (isIframe) {
        writer(`awsccc=${btoa(JSON.stringify(packedCookie))}; domain=${domain}; path=/; max-age=${maxAge}; secure; SameSite=none`);
    } else {
        writer(`awsccc=${btoa(JSON.stringify(packedCookie))}; domain=${domain}; path=/; max-age=${maxAge}; secure; SameSite=Lax`);
    }
    return consentCookie
}

/**
 * Set only the permanent consent cookie without updating the main consent cookie
 * @param ccbaValue The CCBA preference value (true/false)
 * @param domain Domain for the cookie
 * @param writer Cookie writer function
 */
export function setPermanentConsentCookie(
    ccbaValue: boolean,
    domain?: string,
    writer = defaultWriter,
    configurationValidator = validateConfiguration,
    log?: Log
): void {
    if (domain === undefined) {
        domain = isESCPartition() ? DEFAULT_DOMAIN_ESC : DEFAULT_DOMAIN;
    }
    configurationValidator({ domain, log }, 'customizePermanentConsent');
    
    // Create a JSON object with ccba property and version
    const permanentConsent = { 
        ccba: ccbaValue !== false ? 1 : 0, // Ensure undefined defaults to true
        v: COOKIE_VERSION // Version field for future compatibility
    };
    
    // Encode the JSON as base64
    const encodedValue = btoa(JSON.stringify(permanentConsent));
    
    writer(`${PERMANENT_COOKIE_NAME}=${encodedValue}; domain=${domain}; path=/; max-age=${PERMANENT_COOKIE_AGE}; secure; SameSite=Lax`);
}